/**
 * Clinic Types
 * Types for clinic registration, management, and doctor associations
 */

import type { Address } from './user.types';
import type { DoctorProfile } from './doctor.types';

export enum ClinicStatus {
  PENDING_VERIFICATION = 'pending_verification',
  VERIFIED = 'verified',
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
}

export enum ClinicType {
  PRIVATE_PRACTICE = 'private_practice',
  GROUP_PRACTICE = 'group_practice',
  HOSPITAL = 'hospital',
  URGENT_CARE = 'urgent_care',
  SPECIALTY_CLINIC = 'specialty_clinic',
  MULTI_LOCATION = 'multi_location',
}

export interface ClinicRegistrationData {
  clinicName: string;
  legalBusinessName?: string;
  email: string;
  password: string;
  phone: string;
  clinicType: ClinicType;
  npiNumber?: string; // Organization NPI
  address: Address;
  taxId?: string; // EIN or Tax ID
  website?: string;
  agreeToTerms: boolean;
}

// Clinic Onboarding Types
export interface ClinicOnboardingData {
  // Step 1: Clinic Information
  clinicName: string;
  legalBusinessName: string;
  clinicType: ClinicType;
  npiNumber?: string; // Organization NPI
  taxId?: string; // EIN
  website?: string;
  establishedYear?: number;
  
  // Step 2: Primary Clinic Address
  address: Address;
  suite?: string;
  phone: string;
  email: string;
  fax?: string;
  
  // Step 3: Contact Person
  contactPersonName: string;
  contactPersonRole: 'owner' | 'office_manager' | 'admin' | 'doctor';
  contactPersonPhone: string;
  contactPersonEmail: string;
  
  // Step 4: Licensing & Compliance
  stateBusinessLicenseNumber: string;
  medicalPracticeLicenseNumber?: string;
  licenseExpiryDate?: string;
  cliaNumber?: string;
  supportingDocuments?: {
    businessLicense?: string; // S3 URL
    cliaCertificate?: string; // S3 URL
    practiceRegistrationCertificate?: string; // S3 URL
  };
  
  // Step 5: Services Provided
  visitTypes: {
    inClinic: boolean;
    telehealth: boolean;
    homeVisits: boolean;
  };
  specialties: string[]; // Multi-select
  acceptedAgeGroups: {
    children: boolean;
    adults: boolean;
    seniors: boolean;
  };
  languages: string[]; // Multi-select
  
  // Step 6: Operation Details
  workingDays: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  openingTime: string; // HH:mm
  closingTime: string; // HH:mm
  emergencyAvailability: boolean;
  
  // Step 7: Insurance & Billing
  insurancesAccepted: string[]; // Medicare, Medicaid, Private Insurance, Self-Pay
  taxonomyCode?: string;
  w9Form?: string; // S3 URL
  
  // Step 8: Administrator Account (already created during registration)
  // This is handled separately
  
  // Step 9: Documents Upload
  documents: {
    clinicLogo?: string; // S3 URL
    businessLicense?: string; // S3 URL
    w9Form?: string; // S3 URL
    cliaCertificate?: string; // S3 URL
    addressProof?: string; // S3 URL
    accreditationCertificates?: string[]; // S3 URLs (optional)
    facilityPhotos?: string[]; // S3 URLs (optional)
  };
  
  // Step 10: Terms & Compliance
  agreeToHipaa: boolean;
  agreeToTerms: boolean;
  agreeToBAA: boolean; // Business Associate Agreement
}

export interface ClinicProfile {
  id?: string;
  _id?: string; // MongoDB ID
  userId?: string; // Clinic admin user ID
  clinicName: string;
  legalBusinessName?: string;
  email: string;
  phone: string;
  clinicType: ClinicType;
  address?: Address | {
    phone?: string;
    email?: string;
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    zip?: string;
  };
  taxId?: string;
  npiNumber?: string;
  status?: ClinicStatus | string;
  logo?: string;
  website?: string;
  description?: string;
  specialties?: string[];
  visitTypes?: string[]; // Array of 'In-Person', 'Virtual', 'Home Visit'
  ageGroups?: string[]; // Array of 'Children', 'Adults', 'Seniors'
  languages?: string[]; // Array of languages
  operatingHours?: OperatingHours;
  contactPerson?: {
    fullName?: string;
    role?: string;
    phone?: string;
    email?: string;
  };
  licensing?: {
    licenseState?: string;
    stateBusinessLicenseNumber?: string;
    medicalPracticeLicenseNumber?: string;
    licenseExpiryDate?: string;
    cliaNumber?: string;
    supportingDocuments?: {
      businessLicense?: string;
      cliaCertificate?: string;
      practiceRegistrationCertificate?: string;
    };
  };
  services?: {
    visitTypes?: string[];
    specialties?: string[];
    ageGroups?: string[];
    languages?: string[];
  };
  operations?: {
    workingDays?: string[];
    openingTime?: string;
    closingTime?: string;
    emergencyAvailability?: boolean;
  };
  insurance?: {
    providers?: string[];
    taxonomyCode?: string;
    w9Form?: string;
  };
  documents?: {
    logo?: string | null;
    businessLicense?: string | null;
    w9Form?: string | null;
    cliaCertificate?: string | null;
    addressProof?: string | null;
    optionalCertificates?: string[];
    facilityPhotos?: string[];
  };
  terms?: {
    hipaaCompliant?: boolean;
    agreedToTerms?: boolean;
    baaConsent?: boolean;
  };
  settings?: ClinicSettings;
  createdAt?: string;
  updatedAt?: string;
  verifiedAt?: string;
  verifiedBy?: string; // Admin user ID
  profileCompleted?: boolean;
  currentStep?: number;
  createdBy?: string;
  __v?: number;
}

export interface OperatingHours {
  monday?: DaySchedule;
  tuesday?: DaySchedule;
  wednesday?: DaySchedule;
  thursday?: DaySchedule;
  friday?: DaySchedule;
  saturday?: DaySchedule;
  sunday?: DaySchedule;
}

export interface DaySchedule {
  open: boolean;
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
}

export interface ClinicSettings {
  allowDoctorSelfRegistration: boolean; // Doctors can request to join
  requireApprovalForDoctors: boolean; // Clinic admin must approve doctors
  allowDoctorAutonomy: boolean; // Doctors can manage their own schedules
  revenueSharing?: {
    enabled: boolean;
    clinicPercentage: number; // 0-100
    doctorPercentage: number; // 0-100
  };
  notificationPreferences: {
    newDoctorRequest: boolean;
    doctorActivity: boolean;
    appointmentUpdates: boolean;
    paymentUpdates: boolean;
  };
}

export interface ClinicDoctorAssociation {
  id: string;
  clinicId: string;
  doctorId: string;
  doctorUserId: string;
  status: ClinicDoctorStatus;
  role: ClinicDoctorRole;
  joinedAt: string;
  approvedAt?: string;
  approvedBy?: string; // Clinic admin user ID
  doctor?: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
      avatar?: string;
    };
    profile: DoctorProfile;
  };
}

export enum ClinicDoctorStatus {
  PENDING = 'pending', // Waiting for clinic approval
  ACTIVE = 'active', // Active member
  INACTIVE = 'inactive', // Temporarily inactive
  SUSPENDED = 'suspended', // Suspended by clinic
  LEFT = 'left', // Doctor left the clinic
}

export enum ClinicDoctorRole {
  OWNER = 'owner', // Clinic owner/admin
  ADMIN = 'admin', // Clinic administrator
  DOCTOR = 'doctor', // Regular doctor
  ASSOCIATE = 'associate', // Associate doctor
}

export interface ClinicStats {
  totalDoctors: number;
  activeDoctors: number;
  pendingDoctors: number;
  totalAppointments: number;
  todayAppointments: number;
  monthlyRevenue: number;
  monthlyAppointments: number;
  averageRating: number;
  totalReviews: number;
}

export interface ClinicActivity {
  id: string;
  type: ClinicActivityType;
  description: string;
  doctorId?: string;
  doctorName?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export enum ClinicActivityType {
  DOCTOR_JOINED = 'doctor_joined',
  DOCTOR_LEFT = 'doctor_left',
  DOCTOR_APPROVED = 'doctor_approved',
  DOCTOR_SUSPENDED = 'doctor_suspended',
  APPOINTMENT_CREATED = 'appointment_created',
  APPOINTMENT_CANCELLED = 'appointment_cancelled',
  PAYMENT_RECEIVED = 'payment_received',
  PROFILE_UPDATED = 'profile_updated',
}

// Clinic Staff Types
export enum ClinicStaffStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  LEFT = 'left',
}

export interface ClinicStaffAssociation {
  id: string;
  clinicId: string;
  userId: string;
  publicId?: string;
  status: ClinicStaffStatus;
  role: 'doctor' | 'receptionist' | 'admin';
  providerType?: string;
  displayName?: string;
  joinedAt: string;
  approvedAt?: string;
  approvedBy?: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
    phone?: string;
    isActive: boolean;
  };
  profile?: {
    basicInfo?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      dateOfBirth?: string;
      gender?: string;
      photo?: string;
      address?: {
        street?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
      };
    };
    credentials?: {
      licenses?: Array<{
        type: string;
        state: string;
        licenseNumber: string;
        expirationDate?: string;
      }>;
      certifications?: string[];
      skills?: string[];
      yearsOfExperience?: number;
      education?: string;
      experience?: string;
      specializations?: string[];
      additionalExperienceNotes?: string;
    };
    schedule?: {
      employmentDetails?: {
        shiftType?: 'full_time' | 'part_time' | 'contract' | 'per_diem';
        flexibleSchedule?: boolean;
        canWorkWeekends?: boolean;
        canWorkHolidays?: boolean;
      };
      weeklySchedule?: Array<{
        day: string;
        slots: Array<{
          from: string;
          to: string;
        }>;
      }>;
      workingDays?: {
        monday?: boolean;
        tuesday?: boolean;
        wednesday?: boolean;
        thursday?: boolean;
        friday?: boolean;
        saturday?: boolean;
        sunday?: boolean;
      };
      startTime?: string;
      endTime?: string;
      shiftType?: string;
      flexibleSchedule?: boolean;
      canWorkWeekends?: boolean;
      canWorkHolidays?: boolean;
    };
    permissions?: {
      accessLevel?: string;
      permissions?: string[];
      departments?: string[];
      canApproveTimeOff?: boolean;
      canManageStaff?: boolean;
      notes?: string;
    };
  };
}

